import { ChangeEvent, useCallback } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import debounce from 'lodash.debounce'

import { defaultConfig } from 'config/default'
import { cn } from 'utils/style'
import { Label } from 'shared/atoms'
import { WithClassName } from 'shared/types/general.types'

type SearchInputVariant = 'default' | 'filled'

type ISearchInput = WithClassName<{
	placeholder: string
	label: string
	variant?: SearchInputVariant
	onChange: (e: ChangeEvent<HTMLInputElement>) => void
}>

const inputClassNames: Record<SearchInputVariant, string> = {
	default:
		'h-[38px] rounded-lg bg-white pr-3 text-xs shadow-[0_0_4px_rgba(0,0,0,0.08)] focus:shadow-[0_0_5px_#465cff]',
	filled:
		'h-10 rounded-lg border border-border bg-muted pr-4 text-[13px] transition-colors focus:border-primary focus:bg-card focus:ring-2 focus:ring-primary/20'
}

export const SearchInput = ({
	label,
	placeholder,
	className,
	variant = 'default',
	onChange
}: ISearchInput) => {
	const debouncedChangeHandler = useCallback(
		debounce(onChange, defaultConfig.searchDebounceTime),
		[]
	)
	const isFilled = variant === 'filled'

	return (
		<div className={cn(isFilled ? 'w-full' : 'flex flex-col gap-y-2.5', className)}>
			{isFilled ? (
				<label htmlFor="search" className="sr-only">
					{label || placeholder}
				</label>
			) : (
				<Label htmlFor="search" text={label} />
			)}
			<div className="relative">
				<MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 stroke-2 text-muted-foreground" />
				<input
					id="search"
					name="search"
					type="search"
					placeholder={placeholder}
					onChange={debouncedChangeHandler}
					className={cn(
						'w-full pl-9 text-foreground outline-none placeholder:text-muted-foreground',
						inputClassNames[variant]
					)}
				/>
			</div>
		</div>
	)
}
